import {
    selectActiveTradeVisual,
    selectChartedLivePrice,
    useTradingStore,
} from '../store';
import { computePnl, exchangePnlPercent, realtimePnl } from '../utils/pnl';

import { useLivePosition } from './useLivePosition';

interface LivePnL {
    livePnLValue: number;
    livePnLPercent: number;
    /**
     * True when the figures came from the exchange rather than a client
     * estimate. Callers that want to hedge their copy ("≈") can read it; the
     * numbers themselves are already the best available either way.
     */
    isFromExchange: boolean;
}

/**
 * Live PnL for the active position.
 *
 * Prefers Hyperliquid's own `unrealizedPnl` / `returnOnEquity`, straight from
 * `clearinghouseState` via GET /speed/position/:coin. That is the industry
 * convention across the Hyperliquid frontends — the exchange computes entry
 * price, unrealized PnL and ROE precisely so every surface agrees, and a client
 * that recomputes them disagrees with app.hyperliquid.xyz and every other
 * wrapper showing the same position.
 *
 * The client estimate below is the FALLBACK ONLY: demo mode (no backend
 * position exists) and the window before the first position read lands. It is
 * an approximation on three axes, which is exactly why it must not be the
 * primary source:
 *
 *   - `entryPrice` is what the client captured at open, not the exchange's
 *     size-weighted average of fills (they diverge on a partial fill);
 *   - `amount` is the collateral the user asked for, not `marginUsed`;
 *   - `currentValue` is the spring-animated LAST price off the chart, while
 *     Hyperliquid computes PnL from MARK — an oracle fair value, not the last
 *     trade. Mark drives PnL and liquidation; last draws the chart. Crossing
 *     the two is the bug this hook used to ship.
 *
 * Reading the exchange also makes the figure immune to a wrong client-side
 * leverage: `unrealizedPnl` is `side × (mark − entry) × size` and takes no
 * leverage input at all.
 */
export function useLivePnL(currentValue: number): LivePnL {
    const activePosition = useTradingStore(selectActiveTradeVisual);
    const live = useLivePosition();
    // `currentValue` is a spring, and for a few hundred ms after a market switch
    // it is still the PREVIOUS market's price. Extrapolating a position against
    // it is what briefly rendered -$27,855,852.53 (-279,189,353.66%) on a $0.14
    // market. Null here makes realtimePnl fall back to the exchange's own figure
    // until the price is genuinely this market's.
    const chartedPrice = useTradingStore(selectChartedLivePrice);

    // The exchange's own numbers, whenever it has a position for us — carried
    // forward to the current price so the readout tracks the market between the
    // 5s polls instead of stepping. Exact on the exchange's value at every poll;
    // see realtimePnl for why this is a delta and not a recompute.
    if (live.unrealizedPnl !== null) {
        const { pnlValue, pnlPercent } = realtimePnl(
            {
                pnlValue: live.unrealizedPnl,
                pnlPercent: exchangePnlPercent(live),
                anchorPrice: activePosition?.pnlAnchorPrice ?? null,
                size: live.size,
                isLong: live.isLong ?? true,
                marginUsed: live.marginUsed,
            },
            chartedPrice ?? 0,
        );
        return {
            livePnLValue: pnlValue,
            livePnLPercent: pnlPercent,
            isFromExchange: true,
        };
    }

    if (!activePosition) {
        return { livePnLValue: 0, livePnLPercent: 0, isFromExchange: false };
    }

    // the demo / pre-first-read path recomputes from scratch, so it needs a
    // price for THIS market too — the entry it is measured against is this
    // market's, and the previous market's price against it is meaningless.
    const { pnlValue, pnlPercent } = computePnl(
        activePosition.entryPrice,
        chartedPrice ?? currentValue,
        activePosition.type,
        activePosition.amount,
        activePosition.leverage,
    );

    return {
        livePnLValue: pnlValue,
        livePnLPercent: pnlPercent,
        isFromExchange: false,
    };
}
